import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./styles.module.css";
import WorkCard from "../../components/WorkCard";
import { worksItems } from "../../data/works";
import {
  FORMAT_TAGS,
  GENRE_TAGS,
  collectPresentTags,
  filterWorks,
} from "../../data/workFilters";
import Seo from "../../components/Seo";

const Works: React.FC = () => {
  const listRef = useRef<HTMLDivElement | null>(null);
  const [visibleSet, setVisibleSet] = useState<Set<string>>(new Set());
  const [genre, setGenre] = useState<string | null>(null);
  const [format, setFormat] = useState<string | null>(null);

  const genreOptions = useMemo(
    () => collectPresentTags(worksItems, GENRE_TAGS),
    []
  );
  const formatOptions = useMemo(
    () => collectPresentTags(worksItems, FORMAT_TAGS),
    []
  );

  const filtered = useMemo(
    () => filterWorks(worksItems, genre, format),
    [genre, format]
  );

  useEffect(() => {
    // フィルター変更時は表示アニメをやり直す
    setVisibleSet(new Set());
  }, [genre, format]);

  useEffect(() => {
    const root = listRef.current;
    if (!root) return;
    const io = new IntersectionObserver(
      (entries) => {
        setVisibleSet((prev) => {
          const next = new Set(prev);
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const id = (entry.target as HTMLElement).dataset.id;
              if (id) {
                next.add(id);
                io.unobserve(entry.target);
              }
            }
          });
          return next;
        });
      },
      { threshold: 0.15 }
    );
    const targets = root.querySelectorAll(`.${styles.reveal}`);
    targets.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [filtered]);

  const toggleGenre = (label: string | null) => {
    setGenre((prev) => (label === null ? null : prev === label ? null : label));
  };
  const toggleFormat = (label: string | null) => {
    setFormat((prev) =>
      label === null ? null : prev === label ? null : label
    );
  };

  return (
    <div className={styles.page}>
      <Seo title="制作したもの" />
      <div className={styles.titleRow}>
        <img src="/assets/logo/logo_works.svg" alt="works" />
        <div>
          <div className={styles.ja}>制作したもの</div>
          <div className={styles.en}>Works</div>
        </div>
      </div>

      <div className={styles.filters} role="search" aria-label="作品フィルター">
        <div className={styles.filterGroup}>
          <div className={styles.filterLabel}>ジャンル</div>
          <div className={styles.filterChips} role="group" aria-label="ジャンル">
            <button
              type="button"
              className={`${styles.filterChip} ${
                genre === null ? styles.filterChipActive : ""
              }`}
              aria-pressed={genre === null}
              onClick={() => toggleGenre(null)}
            >
              すべて
            </button>
            {genreOptions.map((label) => (
              <button
                key={label}
                type="button"
                className={`${styles.filterChip} ${
                  genre === label ? styles.filterChipActive : ""
                }`}
                aria-pressed={genre === label}
                onClick={() => toggleGenre(label)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.filterGroup}>
          <div className={styles.filterLabel}>形式</div>
          <div className={styles.filterChips} role="group" aria-label="形式">
            <button
              type="button"
              className={`${styles.filterChip} ${
                format === null ? styles.filterChipActive : ""
              }`}
              aria-pressed={format === null}
              onClick={() => toggleFormat(null)}
            >
              すべて
            </button>
            {formatOptions.map((label) => (
              <button
                key={label}
                type="button"
                className={`${styles.filterChip} ${
                  format === label ? styles.filterChipActive : ""
                }`}
                aria-pressed={format === label}
                onClick={() => toggleFormat(label)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div ref={listRef} className={styles.list}>
        {filtered.length === 0 ? (
          <p className={styles.empty}>該当する作品がありません</p>
        ) : (
          filtered.map((w, idx) => (
            <div
              key={w.title}
              data-id={w.title}
              className={`${styles.reveal} ${
                visibleSet.has(w.title) ? styles.revealVisible : ""
              }`}
              style={{ ["--delay" as any]: `${Math.min(idx, 6) * 80}ms` }}
            >
              <WorkCard
                title={w.title}
                image={w.image}
                tags={w.tags}
                period={w.period}
                description={w.description}
                officialUrl={w.officialUrl}
                officialUrlLabel={w.officialUrlLabel}
                hashtag={w.hashtag}
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Works;
